import type { ApiClient } from '@commander/api-client';

/**
 * Singleton ApiClient instance for auth operations
 * This is set once during app initialization via setAuthApiClient()
 *
 * Note: For React components, prefer using useApiClient() from @commander/api-client
 * This instance is primarily for Zustand stores and other non-React code.
 */
let apiClient: ApiClient | null = null;

/**
 * Set the ApiClient instance for auth operations
 * Should be called once during app initialization
 *
 * @example
 * ```ts
 * const apiClient = createApiClient(axiosInstance);
 * setAuthApiClient(apiClient);
 * ```
 */
export function setAuthApiClient(client: ApiClient): void {
  apiClient = client;
}

/**
 * Get the ApiClient instance for auth operations
 * Throws if not initialized - call setAuthApiClient() first
 *
 * @throws Error if ApiClient not initialized
 */
export function getApiClient(): ApiClient {
  if (!apiClient) {
    throw new Error(
      'ApiClient not initialized. Call setAuthApiClient(apiClient) during app initialization ' +
        '(typically in main.tsx after creating the ApiClient instance).'
    );
  }
  return apiClient;
}

/**
 * Check if ApiClient has been initialized
 */
export function isApiClientInitialized(): boolean {
  return apiClient !== null;
}

/**
 * Reset the ApiClient instance (primarily for testing)
 */
export function resetAuthApiClient(): void {
  apiClient = null;
}
